import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Bell, BellOff, RefreshCw, AlertTriangle, ArrowLeft } from 'lucide-react';
import { alertsApi } from '../../api/endpoints/alerts';
import type { AlertRule, AlertRulePayload } from '../../api/endpoints/alerts';
import { usersApi } from '../../api/endpoints/users';
import { queryClient } from '../../providers/QueryProvider';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate } from '../../utils/formatters';

interface RuleFormData {
  name: string;
  metric: string;
  operator: string;
  threshold: number;
  duration_minutes: number;
  severity: 'warning' | 'critical';
  cooldown_minutes: number;
  enabled: boolean;
  recipients: string[]; // user IDs
}

const defaultForm: RuleFormData = {
  name: '',
  metric: 'cpu_percent',
  operator: 'gt',
  threshold: 80,
  duration_minutes: 5,
  severity: 'warning',
  cooldown_minutes: 30,
  enabled: true,
  recipients: [],
};

const metricLabels: Record<string, string> = {
  cpu_percent: 'CPU %',
  memory_percent: 'Memory %',
  disk_percent: 'Disk %',
  disconnect: 'Disconnection (min)',
  api_error_rate: 'API Error Rate %',
};

const operatorLabels: Record<string, string> = {
  gt: '>',
  lt: '<',
};

export function AlertRulesPage() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleFormData>(defaultForm);
  const [formError, setFormError] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () => alertsApi.listRules(),
  });

  // Fetch users for recipient selection
  const { data: usersData } = useQuery({
    queryKey: ['users-for-recipients'],
    queryFn: () => usersApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: AlertRulePayload) => alertsApi.createRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message || 'Failed to create rule'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AlertRulePayload }) =>
      alertsApi.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message || 'Failed to update rule'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertsApi.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      setDeleteConfirmId(null);
    },
    onError: (err: Error) => {
      setFormError(err.message || 'Failed to delete rule');
      setDeleteConfirmId(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      alertsApi.updateRule(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    },
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(defaultForm);
    setFormError('');
  };

  const openCreate = () => {
    setForm(defaultForm);
    setEditingId(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (rule: AlertRule) => {
    setForm({
      name: rule.name,
      metric: rule.metric,
      operator: rule.operator,
      threshold: rule.threshold,
      duration_minutes: rule.duration_minutes,
      severity: rule.severity,
      cooldown_minutes: rule.cooldown_minutes,
      enabled: rule.enabled,
      recipients: rule.recipients.map(r => r._id),
    });
    setEditingId(rule._id);
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim()) {
      setFormError('Rule name is required');
      return;
    }

    const payload = {
      name: form.name.trim(),
      metric: form.metric,
      operator: form.operator,
      threshold: form.threshold,
      duration_minutes: form.duration_minutes,
      severity: form.severity,
      cooldown_minutes: form.cooldown_minutes,
      enabled: form.enabled,
      recipients: form.recipients,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeleteClick = (rule: AlertRule) => {
    setDeleteConfirmId(rule._id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
  };

  const toggleRecipient = (userId: string) => {
    setForm(f => ({
      ...f,
      recipients: f.recipients.includes(userId)
        ? f.recipients.filter(id => id !== userId)
        : [...f.recipients, userId],
    }));
  };

  const rules = data?.rules || [];
  const users = usersData?.users || [];
  const usersWithEmail = users.filter(u => u.email);
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const ruleToDelete = rules.find(r => r._id === deleteConfirmId);

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/analytics')}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Alert Rules</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
              Configure threshold-based alert rules and notifications
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={openCreate}
            className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs"
          >
            <Plus size={14} />
            Add Rule
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-lg px-3 py-2 bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          <p className="text-xs font-medium opacity-75">Total Rules</p>
          <p className="text-lg font-bold">{rules.length}</p>
        </div>
        <div className="rounded-lg px-3 py-2 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
          <p className="text-xs font-medium opacity-75">Active</p>
          <p className="text-lg font-bold">{rules.filter(r => r.enabled).length}</p>
        </div>
        <div className="rounded-lg px-3 py-2 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
          <p className="text-xs font-medium opacity-75">Warning</p>
          <p className="text-lg font-bold">{rules.filter(r => r.severity === 'warning').length}</p>
        </div>
        <div className="rounded-lg px-3 py-2 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400">
          <p className="text-xs font-medium opacity-75">Critical</p>
          <p className="text-lg font-bold">{rules.filter(r => r.severity === 'critical').length}</p>
        </div>
      </div>

      {/* Rules Table */}
      <Card>
        <Table>
          <Thead>
            <tr>
              <Th>Name</Th>
              <Th>Metric</Th>
              <Th>Condition</Th>
              <Th>Duration</Th>
              <Th>Severity</Th>
              <Th>Cooldown</Th>
              <Th>Status</Th>
              <Th>Recipients</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {rules.map(rule => (
              <Tr key={rule._id}>
                <Td>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{rule.name}</span>
                </Td>
                <Td>
                  <Badge
                    variant={
                      rule.metric === 'cpu_percent' ? 'red'
                        : rule.metric === 'memory_percent' ? 'yellow'
                        : 'blue'
                    }
                  >
                    {metricLabels[rule.metric] || rule.metric}
                  </Badge>
                </Td>
                <Td>
                  <span className="font-mono text-xs">
                    {operatorLabels[rule.operator] || rule.operator} {rule.threshold}{rule.metric === 'disconnect' ? ' min' : '%'}
                  </span>
                </Td>
                <Td>{rule.duration_minutes}m</Td>
                <Td>
                  <Badge variant={rule.severity === 'critical' ? 'red' : 'yellow'}>
                    {rule.severity}
                  </Badge>
                </Td>
                <Td>{rule.cooldown_minutes}m</Td>
                <Td>
                  <button
                    onClick={() => toggleMutation.mutate({ id: rule._id, enabled: !rule.enabled })}
                    disabled={toggleMutation.isPending}
                    className="flex items-center gap-1"
                    title={rule.enabled ? 'Click to disable' : 'Click to enable'}
                  >
                    {rule.enabled ? (
                      <Bell size={14} className="text-green-500" />
                    ) : (
                      <BellOff size={14} className="text-gray-400" />
                    )}
                    <Badge variant={rule.enabled ? 'green' : 'gray'}>
                      {rule.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </button>
                </Td>
                <Td>
                  {rule.recipients.length > 0 ? (
                    <span className="text-xs text-gray-600 dark:text-gray-400" title={rule.recipients.map(r => r.email || r.username).join(', ')}>
                      {rule.recipients.map(r => r.username).join(', ')}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400 italic">None</span>
                  )}
                </Td>
                <Td>
                  <span className="text-gray-500 dark:text-gray-400">
                    {rule.createdAt ? formatDate(rule.createdAt) : '-'}
                  </span>
                  {rule.created_by && (
                    <p className="text-[10px] text-gray-400">by {rule.created_by.username}</p>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(rule)}
                      className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium flex items-center gap-0.5"
                    >
                      <Edit size={12} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(rule)}
                      disabled={deleteMutation.isPending}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium flex items-center gap-0.5"
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  </div>
                </Td>
              </Tr>
            ))}
            {rules.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-8">
                  <AlertTriangle size={24} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No alert rules configured. Click "Add Rule" to create one.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Edit Alert Rule' : 'Add Alert Rule'}
        wide
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-3 py-2 rounded-md text-xs">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rule Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
              placeholder="e.g., High CPU Alert"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Metric
              </label>
              <select
                value={form.metric}
                onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
              >
                <option value="cpu_percent">CPU %</option>
                <option value="memory_percent">Memory %</option>
                <option value="disk_percent">Disk %</option>
                <option value="disconnect">Disconnection (minutes offline)</option>
                <option value="api_error_rate">API Error Rate %</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Operator
              </label>
              <select
                value={form.operator}
                onChange={e => setForm(f => ({ ...f, operator: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
              >
                <option value="gt">Greater than (&gt;)</option>
                <option value="lt">Less than (&lt;)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Threshold ({form.metric === 'disconnect' ? 'minutes offline' : '%'})
              </label>
              <input
                type="number"
                min={0}
                max={form.metric === 'disconnect' ? 1440 : 100}
                value={form.threshold}
                onChange={e => setForm(f => ({ ...f, threshold: Number(e.target.value) }))}
                required
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (minutes)
              </label>
              <input
                type="number"
                min={1}
                value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))}
                required
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
              />
              <p className="text-[10px] text-gray-400 mt-0.5">
                How long metric must exceed threshold before alerting
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Severity
              </label>
              <select
                value={form.severity}
                onChange={e => setForm(f => ({ ...f, severity: e.target.value as 'warning' | 'critical' }))}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white"
              >
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cooldown (minutes)
              </label>
              <input
                type="number"
                min={1}
                value={form.cooldown_minutes}
                onChange={e => setForm(f => ({ ...f, cooldown_minutes: Number(e.target.value) }))}
                required
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
              />
              <p className="text-[10px] text-gray-400 mt-0.5">
                Minimum wait between repeat notifications
              </p>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notification Recipients
            </label>
            {usersWithEmail.length > 0 ? (
              <div className="border border-gray-300 dark:border-gray-600 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                {usersWithEmail.map(user => (
                  <label
                    key={user._id}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.recipients.includes(user._id)}
                      onChange={() => toggleRecipient(user._id)}
                      className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{user.username}</span>
                    <span className="text-[10px] text-gray-400">{user.email}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic px-2 py-1">
                No users with email addresses found. Add emails to users to enable notifications.
              </p>
            )}
            {form.recipients.length > 0 && (
              <p className="text-[10px] text-gray-400 mt-1">
                {form.recipients.length} recipient{form.recipients.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rule-enabled"
              checked={form.enabled}
              onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
            />
            <label htmlFor="rule-enabled" className="text-xs text-gray-700 dark:text-gray-300">
              Enable this rule immediately
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={closeModal}
              className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : editingId ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Alert Rule"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete the rule{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              "{ruleToDelete?.name}"
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeleteConfirmId(null)}
              className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Rule'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
